import { z } from 'zod';

// Zod schemas for validation

export const ApiServiceSchema = z.object({
  id: z.string(),
  name: z.string(),
  host: z.string(),
  port: z.number().int().min(1).max(65535),
  path: z.string().optional(),
  protocol: z.enum(['http', 'https']),
  tags: z.array(z.string()),
  metrics: z.object({
    rqps: z.number(),
    p95: z.number(),
    '5xx': z.number(),
  }).optional(),
  healthy: z.boolean(),
  routes: z.array(z.string()),
  plugins: z.array(z.string()),
});

export const ApiRouteSchema = z.object({
  id: z.string(),
  serviceId: z.string(),
  paths: z.array(z.string().startsWith('/')),
  methods: z.array(z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'])),
  strip_path: z.boolean(),
  preserve_host: z.boolean(),
  cors: z.object({
    enabled: z.boolean(),
    origins: z.array(z.string()),
  }).optional(),
  auth: z.enum(['jwt', 'none', 'key']),
  limits: z.object({
    minute: z.number().int().positive().optional(),
    sizeMB: z.number().positive().optional(),
  }).optional(),
  traffic: z.object({
    rqps: z.number(),
    p95: z.number(),
  }).optional(),
});

export const ApiPluginSchema = z.object({
  id: z.string(),
  serviceId: z.string().optional(),
  routeId: z.string().optional(),
  name: z.string(),
  config: z.record(z.any()),
});

export const ApiCatalogSchema = z.object({
  services: z.array(ApiServiceSchema),
  routes: z.array(ApiRouteSchema),
  plugins: z.array(ApiPluginSchema),
});

export const TestRequestSchema = z.object({
  method: z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS']),
  url: z.string().url(),
  headers: z.record(z.string()),
  jwt: z.string().optional(),
  body: z.any().optional(),
  timeoutMs: z.number().int().positive().max(60000),
  followRedirects: z.boolean(),
});

export const TestResponseSchema = z.object({
  ok: z.boolean(),
  status: z.number().int().min(100).max(599),
  timing: z.object({
    totalMs: z.number(),
  }),
  headers: z.record(z.string()),
  body: z.any(),
});

export const CreateServiceFormSchema = z.object({
  name: z.string().min(1).max(100).regex(/^[a-z0-9-]+$/, 'Must be lowercase alphanumeric with hyphens'),
  host: z.string().min(1),
  port: z.number().int().min(1).max(65535),
  protocol: z.enum(['http', 'https']),
  path: z.string().startsWith('/'),
  tags: z.array(z.string()),
});

export const CreateRouteFormSchema = z.object({
  paths: z.array(z.string().startsWith('/')).min(1, 'At least one path required'),
  methods: z.array(z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH', 'HEAD', 'OPTIONS'])).min(1, 'At least one method required'),
  strip_path: z.boolean(),
  preserve_host: z.boolean(),
  auth: z.enum(['jwt', 'none', 'key']),
  rateLimitMinute: z.number().int().positive().max(10000),
  requestSizeMB: z.number().positive().max(100),
  corsEnabled: z.boolean(),
  corsOrigins: z.array(z.string()),
});

export const CreateApiFormSchema = z.object({
  source: z.enum(['new', 'existing']),
  existingServiceId: z.string().optional(),
  service: CreateServiceFormSchema,
  route: CreateRouteFormSchema,
}).refine(data => {
  if (data.source === 'existing') {
    return !!data.existingServiceId;
  }
  return true;
}, {
  message: 'Must select an existing service when source is "existing"',
  path: ['existingServiceId'],
});

export const DryRunResponseSchema = z.object({
  policy: z.array(z.object({
    level: z.enum(['info', 'warn', 'error']),
    message: z.string(),
    path: z.string().optional(),
  })),
  impact: z.object({
    summary: z.string(),
    affected: z.array(z.string()),
  }),
  normalizedSpec: z.array(z.object({
    kind: z.enum(['ApiService', 'ApiRoute', 'ApiPlugin']),
    id: z.string(),
    ops: z.array(z.object({
      op: z.enum(['add', 'remove', 'replace']),
      path: z.string(),
      value: z.any().optional(),
    })),
  })),
});

export const CatalogFiltersSchema = z.object({
  search: z.string(),
  health: z.enum(['all', 'healthy', 'unhealthy']),
  auth: z.enum(['all', 'jwt', 'none', 'key']),
  tenant: z.string(),
  plugin: z.string(),
});

// Default values
export const defaultCreateApiForm: z.infer<typeof CreateApiFormSchema> = {
  source: 'new',
  service: {
    name: '',
    host: '',
    port: 8080,
    protocol: 'http',
    path: '/',
    tags: [],
  },
  route: {
    paths: ['/api/'],
    methods: ['GET'],
    strip_path: false,
    preserve_host: true,
    auth: 'jwt',
    rateLimitMinute: 600,
    requestSizeMB: 2,
    corsEnabled: true,
    corsOrigins: ['*'],
  },
};

export const defaultTestRequest: z.infer<typeof TestRequestSchema> = {
  method: 'GET',
  url: 'https://dev.devcockpit.ai/api/',
  headers: {
    'x-tenant-id': 'dev',
  },
  timeoutMs: 10000,
  followRedirects: false,
};

export const defaultCatalogFilters: z.infer<typeof CatalogFiltersSchema> = {
  search: '',
  health: 'all',
  auth: 'all',
  tenant: '',
  plugin: '',
};
